import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useState } from "react";
import useBillingEventUploadFiscalDocumentForConsumption from "../../hooks/useBillingEventUploadFiscalDocumentForConsumption";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import Checkbox from "@/common/components/Checkbox";
import { MultipleDocumentUploader } from "@/common/components/MultipleDocumentUploader";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { useForm } from "react-hook-form";
import { uploadFiscalDocumentsSchema, UploadFiscalDocumentsValidationSchema } from "../../schemas/uploadFiscalDocuments.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorMessage } from "@/common/components/ErrorMessage";

const UploadFiscalDocument = ({ eventId }: { eventId: string; }) => {
  const queryClient = useQueryClient();

  const {
    control,
    reset,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<UploadFiscalDocumentsValidationSchema>({
    resolver: zodResolver(uploadFiscalDocumentsSchema)
  })

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { mutate: uploadFiscalDocumentForConsumption } = useBillingEventUploadFiscalDocumentForConsumption({
    id: eventId,
    onSuccess: () => {
      setModalIsOpen(false);
      setIsError(false);
      reset()
      setMessages(["Nota enviada com sucesso!"]);
    },
    onError: (error) => {
      if (error.response?.data.errors) {
        setModalIsOpen(false);
        setIsError(true);
        reset()
        setMessages(Object.values(error.response?.data.errors).flat());
      }
    }
  });

  const onSubmit = (data: UploadFiscalDocumentsValidationSchema) => {
    uploadFiscalDocumentForConsumption(data);
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        setIsOpen={(value) => {
          setModalIsOpen(value);
          reset()
        }}
        Trigger={(
          <Button>Upload de nota</Button>
        )}
        title="Nova nota fiscal"
        footer="Confirmar upload manual"
        footerClassName="bg-primary-blue text-white-800"
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col max-h-[400px] overflow-y-auto thin-scroll px-1">
              <MultipleDocumentUploader
                className="flex-col w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
                containerClassName="flex-col-reverse"
                fileListClassName="flex-col"
                fileItemClassName="w-full text-white-400"
                title="Anexe a nota fiscal"
                control={control}
                name="fiscal_documents"
                fieldName="fiscal_documents"
                uploadBlob={uploadBlob}
                accept=".xml"
                disabled={uploadBlobIsPending}
              />

              {errors.fiscal_documents?.message && (
                <ErrorMessage
                  name="file"
                  message={errors.fiscal_documents?.message}
                />
              )}
            </div>

            <Checkbox
              label="Ignorar validações de produto e valor"
              className="text-white-800"
              {...register('skip_product_validations')}
            />
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingEvent', eventId] });
            queryClient.invalidateQueries({ queryKey: ['billingEvents'] });
          }}
        />
      )}
    </>
  );
};

export default UploadFiscalDocument;
